// react
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, 
    Button, 
    TextField, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer,
    TableHead, 
    TableRow, 
    Paper, 
    Dialog, 
    DialogTitle, 
    DialogContent,
    DialogActions,
    Select,
    MenuItem
} from '@mui/material';
// service
import { 
    fetchUsers,
    createUser,
    updateUser,
    deleteUser,
    initPassword,
    changePasswordApi
} from '../service/user-api';
// type
import { User } from '../types/user';
import { ChangePassword } from '../types/changePassword';
import { ChangePasswordRequest } from '../types/changePasswordRequest';
import { ChallengeResponse } from '../types/challengeResponse';
// const
import constant from '../common/const';

/**
 * USER画面
 * @returns 
 */
const Users = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [searchText, setSearchText] = useState('');
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentUser, setCurrentUser] = useState<User>({ userId: '', name: '', address: '', role: 'NORMAL' }); // フォームに設定する値
    const [changePassword, setChangePassword] = useState<ChangePassword>({ oldPassword: '', newPassword: ''} );
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmActionType, setConfirmActionType] = useState<'create' | 'update' | 'delete' | 'initPassword' | 'changePassword' | null>(null);
    const [pendingUser, setPendingUser] = useState<User | null>(null); // 確定したユーザ情報
    const [doName, setDoName] = useState<string>("");
    const [subjectName, setSubjectName] = useState<string>("");
    const [loginUser, setLoginUser] = useState<User>({ userId: '', name: '', address: '', role: 'NORMAL' });

    const hasFetched = useRef(false);

    useEffect(() => {

        // ログインユーザ
        const authRaw = localStorage.getItem("auth");
        if (authRaw) {
            const authData: ChallengeResponse = JSON.parse(authRaw) as ChallengeResponse;
            setLoginUser(authData.user);
            // setLoginUser({userId: '', name: '', email: '', role: 'NORMAL'});

            // アドミンのときのみ呼び出し
            if (authData.user.role === constant.AUTH.ADMIN && !hasFetched.current) {
                // フラグを立てる
                hasFetched.current = true;
                const getUsers = async () => {
                    const data = await fetchUsers();
                    // 失敗した場合は処理終了
                    if (!data) {
                        return;
                    }
                    setUsers(data);
                };
                // ユーザー一覧呼び出し
                getUsers();
            }
        }
    }, []);

    /**
     * 
     */
    const filteredUsers = users.filter(u =>
        u.name?.includes(searchText) || u.address?.includes(searchText)
    );

    /**
     * 🔵 ユーザ入力ダイアログオープン時
     * @param user
     */
    const onClickUserDialogOpen = (user?: User) => {
        setErrors({});
        if (user) {
            setCurrentUser(user);
            setIsEditMode(true);
            setDoName("更新");
        } else {
            setCurrentUser({ userId: '', name: '', address: '', role: 'NORMAL' });
            setIsEditMode(false);
            setDoName("作成");
        }
        setUserDialogOpen(true);
    };

    /**
     * 🔴 作成・更新ボタン押下時
     * @returns 
     */
    const onClickUserSave = () => {
        // バリデーション実施
        if (!userValidate()) return;

        setPendingUser(currentUser);
        setConfirmActionType(isEditMode ? 'update' : 'create');
        setSubjectName(currentUser.name);
        setConfirmOpen(true);
    };

    /**
     * 🟡 ユーザーダイアログ閉じる ボタン押下時
     */
    const onClickUserDialogClose = () => {
        setUserDialogOpen(false);
        setErrors({});
        setCurrentUser({ userId: '', name: '', address: '', role: 'NORMAL' });
    };

    /**
     * 🔴 ユーザー削除時
     * @param userId 
     * @returns 
     */
    const onClickDelete = (userId: string) => {
        const user = users.find(u => u.userId === userId);
        if (!user) return;

        setPendingUser(user);
        setConfirmActionType('delete');
        setDoName("削除");
        setSubjectName(user.name);
        setConfirmOpen(true);
    };

    /**
     * 🔵 パスワード変更ダイアログオープン時
     * @param user
     */
    const handlePasswordDialogOpen = () => {
        setErrors({});
        // パスワード変更の場合はログインユーザを設定
        setPendingUser(loginUser);
        setPasswordDialogOpen(true);
    };

    /**
     * 🔴 パスワード変更クリック時
     * @returns 
     */
    const onClickChangePassword = () => {
        // バリデーション実施
        if (!passwordValidate()) return;

        setPendingUser(currentUser);
        setConfirmActionType('changePassword');
        setDoName("パスワード変更");
        setConfirmOpen(true);
    };

    /**
     * 🟡 パスワードダイアログ閉じる ボタン押下時
     */
    const onClickPasswordDialogClose = () => {
        setPasswordDialogOpen(false);
        setErrors({});
    };

    /**
     * 🔴 パスワード初期化
     * @param userId 
     * @returns 
     */
    const onClickInitPassword = (userId: string) => {
        const user = users.find(u => u.userId === userId);
        if (!user) return;

        setPendingUser(user);
        setConfirmActionType('initPassword');
        setDoName("パスワード初期化");
        setSubjectName(user.name);
        setConfirmOpen(true);
    };

    /**
     * バリデーション
     * @returns バリデーション結果(boolean)
     */
    const userValidate = (): boolean => {
        const newErrors: { [key: string]: string } = {};
        if (!isEditMode && !currentUser.userId.trim()) {
            newErrors.id = 'IDを入力してください';
        } else if (!isEditMode && users.some(u => u.userId === currentUser.userId)) {
            newErrors.id = 'そのIDはすでに使われています';
        }

        if (!currentUser.name.trim()) {
            newErrors.name = 'ユーザー名を入力してください';
        }

        if (!currentUser.address.trim()) {
            newErrors.email = 'メールアドレスを入力してください';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentUser.address)) {
            newErrors.email = '正しいメールアドレス形式で入力してください';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * パスワードバリデーション
     * @returns バリデーション結果(boolean)
     */
    const passwordValidate = (): boolean => {
        const newErrors: { [key: string]: string } = {};
        if (!changePassword.oldPassword.trim()) {
            newErrors.oldPassword = '旧パスワードを入力してください';
        } else if (!/^[\x20-\x7E]+$/.test(changePassword.oldPassword)) {
            newErrors.oldPassword = '半角英数字記号で入力してください';
        }

        if (!changePassword.newPassword.trim()) {
            newErrors.newPassword = '新パスワードを入力してください';
        } else if (!/^[\x20-\x7E]+$/.test(changePassword.newPassword)) {
            newErrors.newPassword = '半角英数字記号で入力してください';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * 確認ダイアログ押下時
     * @returns 
     */
    const handleConfirm = async () => {
        if (!pendingUser || !confirmActionType) return;

        if (confirmActionType === 'create') {
            // ユーザー作成
            const newUser: User  = await createUser(pendingUser);
            // 失敗した場合は処理終了
            if (!newUser) {
                return;
            }
            setUsers([...users, newUser]);
        } else if (confirmActionType === 'update') {
            // ユーザー更新
            const updatedUser: User = await updateUser(pendingUser);
            // 失敗した場合は処理終了
            if (!updatedUser) {
                return;
            }
            setUsers(users.map(u => u.userId === updatedUser.userId ? updatedUser : u));
        } else if (confirmActionType === 'delete') {
            const data = await deleteUser(pendingUser.userId);
            // 失敗時は処理終了
            if (!data) {
                return;
            }
            setUsers(users.filter(u => u.userId !== pendingUser.userId));
        } else if (confirmActionType === 'initPassword') {
            const userId = { userId: pendingUser.userId }
            const data = await initPassword(userId);
            // 失敗時は処理終了
            if (!data) {
                return;
            }
        } else if (confirmActionType === 'changePassword') {
            const authRaw = localStorage.getItem("auth");
            if (authRaw) {
                const authData: ChallengeResponse = JSON.parse(authRaw) as ChallengeResponse;
                const changePasswordRequest: ChangePasswordRequest = {
                    userId: authData.user.userId,
                    oldPassword: changePassword.oldPassword,
                    newPassword: changePassword.newPassword,
                    accessToken: authData.accessToken,
                }
                const data = await changePasswordApi(changePasswordRequest);
                // 失敗時は処理終了
                if (!data) {
                    return;
                }
            }
        }

        setConfirmOpen(false);
        setPendingUser(null);
        setChangePassword({ oldPassword: '', newPassword: ''});
        onClickUserDialogClose();
        onClickPasswordDialogClose();
    };

    return (
        <>
            <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 5 }}>
                { loginUser.role === 'ADMIN' &&
                    <TextField
                        fullWidth label="検索" margin="normal"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                }
                {/* ボタン */}
                <div className='user-nav-wrapper'>
                    { loginUser.role === 'ADMIN' &&
                        <div className='user-nav'>
                            <Button variant="contained" color="primary" onClick={() => onClickUserDialogOpen()}>
                                新規作成
                            </Button>
                        </div>
                    }

                    <div className='user-nav'>
                        <Button variant="contained" color="primary" onClick={() => handlePasswordDialogOpen()}>
                            パスワード変更
                        </Button>
                    </div>
                </div>
                { loginUser.role === 'ADMIN' &&
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>ユーザー名</TableCell>
                                    <TableCell>メールアドレス</TableCell>
                                    <TableCell>ロール</TableCell>
                                    <TableCell>初期化</TableCell>
                                    <TableCell>削除</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredUsers && filteredUsers.map((user) => (
                                    <TableRow key={user.userId} onClick={() => onClickUserDialogOpen(user)} sx={{ cursor: 'pointer' }}>
                                        <TableCell>{user.userId}</TableCell>
                                        <TableCell>{user.name}</TableCell>
                                        <TableCell>{user.address}</TableCell>
                                        <TableCell>{user.role}</TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Button variant="contained" color="secondary" onClick={() => onClickInitPassword(user.userId)}>
                                                初期化
                                            </Button>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Button variant="contained" color="secondary" onClick={() => onClickDelete(user.userId)}>
                                                削除
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                }

                {/* ユーザ作成・編集ダイアログ */}
                <Dialog open={userDialogOpen} onClose={onClickUserDialogClose}>
                    <DialogTitle>{isEditMode ? 'ユーザー編集' : '新規ユーザー作成'}</DialogTitle>
                    <DialogContent>
                        {!isEditMode && (
                            <TextField 
                                fullWidth 
                                label="ID" 
                                margin="normal" 
                                value={currentUser.userId}
                                onChange={(e) => setCurrentUser({ ...currentUser, userId: e.target.value })}
                                error={!!errors.id} 
                                helperText={errors.id} 
                            />
                        )}
                        <TextField 
                            fullWidth 
                            label="名前" 
                            margin="normal" 
                            value={currentUser.name}
                            onChange={(e) => setCurrentUser({ ...currentUser, name: e.target.value })}
                            error={!!errors.name} 
                            helperText={errors.name} 
                        />
                        <TextField 
                            fullWidth 
                            label="メールアドレス" 
                            margin="normal" 
                            value={currentUser.address}
                            onChange={(e) => setCurrentUser({ ...currentUser, address: e.target.value })}
                            error={!!errors.email} 
                            helperText={errors.email} 
                        />
                        <Select 
                            fullWidth 
                            value={currentUser.role}
                            onChange={(e) => setCurrentUser({ ...currentUser, role: e.target.value as User['role'] })}
                        >
                            <MenuItem value="ADMIN">管理者</MenuItem>
                            <MenuItem value="NORMAL">一般</MenuItem>
                        </Select>

                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClickUserDialogClose}>キャンセル</Button>
                        <Button variant="contained" color="primary" onClick={onClickUserSave}>
                            {doName}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* パスワード変更ダイアログ */}
                <Dialog open={passwordDialogOpen} onClose={onClickPasswordDialogClose}>
                    <DialogTitle>パスワード変更</DialogTitle>
                    <DialogContent>
                        <TextField 
                            fullWidth 
                            label="旧パスワード" 
                            margin="normal" 
                            value={changePassword.oldPassword}
                            onChange={(e) => setChangePassword({ ...changePassword, oldPassword: e.target.value })}
                            error={!!errors.oldPassword} 
                            helperText={errors.oldPassword} 
                        />
                        <TextField 
                            fullWidth 
                            label="新パスワード" 
                            margin="normal" 
                            value={changePassword.newPassword}
                            onChange={(e) => setChangePassword({ ...changePassword, newPassword: e.target.value })}
                            error={!!errors.newPassword} 
                            helperText={errors.newPassword} 
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClickPasswordDialogClose}>キャンセル</Button>
                        <Button variant="contained" color="primary" onClick={onClickChangePassword}>
                            更新
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* 確認ダイアログ */}
                <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                    <DialogTitle>確認</DialogTitle>
                    <DialogContent>
                        <p>{pendingUser ? `${subjectName}を${doName}しますか？` : "操作を確認してください"}</p>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmOpen(false)}>キャンセル</Button>
                        <Button onClick={handleConfirm} color="primary" variant="contained">OK</Button>
                    </DialogActions>
                </Dialog>
                {/* ボタン */}

            </Box>
            <div className='nav-button-wrapper'>
                <Button className='nav-button' component={Link} to="/schedule" variant="contained" color="primary">
                    戻る
                </Button>
            </div>
        </>
    );
};

export default Users;